import React, { useState, useEffect } from "react"

//controls
import { Button, Col, Row, Label } from "reactstrap"

//react form
import { FormProvider, useForm, Controller } from "react-hook-form"

//navigation
import { Link, useNavigate, useParams } from "react-router-dom"

//validation
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"

//redux
import { useSelector, useDispatch } from "react-redux"

//custom hooks
import { useDeepCompareEffect, useUserDetail } from "hooks"

import Select from "react-select"

//Date filter
import Moment from "moment"

import {
  addHealthday as onAddHealthday,
  updateHealthday as onUpdateHealthday,
} from "store/actions"
import Spinners from "components/Common/Spinner"
import { isEmpty } from "lodash"

import {
  TextInput,
  DropdownInput,
  DateInput,
  NumberInput,
  ToggleInput,
} from "components/Common/Controls"
import { EnumRoleTypes } from "helpers/enum_helper"

function BasicInfoTab({ healthday, loading }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(loading)
  const [state, setState] = useState({
    selectedSubjects: [],
  })

  const updateState = data => setState(previous => ({ ...previous, ...data }))

  //////////////////HEALTHDAY STATE/////////////////////
  const { centres, ethnicities, phonecontracts, ndbnetworktypes } = useSelector(
    state => state.healthday
  )
  ///////////////////////////////////////

  //////////////////HELPER STATE/////////////////////
  const { genders } = useSelector(state => state.user)
  ///////////////////////////////////////

  /**
   * Form Validation Schema
   */
  const schema = yup.object().shape({
    Centres: yup.object().required("Required"),
    Ethnicities: yup.object().required("Required"),
    PhoneContracts: yup.object(),
    NDBNetworkTypes: yup.object(),
    Genders: yup.object().required("Required"),
    FirstName: yup.string().nullable(),
    LastName: yup.string().nullable(),
    DateOfBirth: yup.string().required("Required"),
    Address: yup.string().nullable(),
    Area: yup.string().required("Required"),
    Postcode: yup.string().required("Required"),
    ContactNumber: yup.string().nullable(),
    MobileNetwork: yup.string().nullable(),
    MMPGroup: yup.string().nullable(),
    EmailAddress: yup.string().nullable(),
    NDBNumber: yup.string().nullable(),
    NDBAdded: yup.bool(),
    Mobile: yup.bool(),
    SmokeAlarm: yup.bool(),
    NDBRegistered: yup.bool(),
    IsActive: yup.bool(),
  })

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      FirstName: "",
      LastName: "",
      DateOfBirth: "",
      Address: "",
      Area: "",
      Postcode: "",
      ContactNumber: "",
      MobileNetwork: "",
      MMPGroup: "",
      EmailAddress: "",
      NDBNumber: "",
      NDBAdded: false,
      Mobile: false,
      SmokeAlarm: false,
      NDBRegistered: false,
      IsActive: true,
    },
    resolver: yupResolver(schema),
  })
  const { reset, control, formState, setValue, getValues, trigger, watch } =
    methods
  const { errors, isValid } = formState

  useDeepCompareEffect(() => {
    function updateProfileState() {
      //reset form with server data
      reset(healthday)
    }

    updateProfileState()
  }, [dispatch])

  const onCancelClick = () => {
    navigate("/healthdays")
  }

  const handleSave = () => {
    trigger() //for form validation
    if (isValid) {
      if (isEmpty(healthday)) {
        dispatch(onAddHealthday(getValues()))
      } else {
        dispatch(onUpdateHealthday(getValues()))
      }
      if (!isEmpty(id)) {
        navigate("/healthdays")
      }
    }
  }

  if (isLoading) {
    return <Spinners setLoading={setLoading} />
  }

  return (
    <FormProvider {...methods}>
      <Row>
        <Col sm="4">
          <div className="mb-3">
            <DropdownInput
              label="Please select your School"
              id="Centres"
              required={true}
              items={centres}
              itemlabel="Centre"
              itemvalue="CentreID"
            />
          </div>
          <div className="mb-3">
            <TextInput label="Enter your first name" id="FirstName" />
          </div>
          <div className="mb-3">
            <DateInput
              label="Enter your date of birth"
              id="DateOfBirth"
              required={true}
            />
          </div>
          <div className="mb-3">
            <DropdownInput
              label="Please select your ethnicity"
              id="Ethnicities"
              required={true}
              items={ethnicities}
              itemlabel="Ethnicity"
              itemvalue="EthnicityID"
            />
          </div>
          <div className="mb-3">
            <TextInput label="Area" id="Area" required={true} />
          </div>
          <div className="mb-2">&nbsp;</div>
          <div className="mb-3">
            <ToggleInput label="Do you use a mobile phone?" id="Mobile" />
          </div>
          <div className="mb-3">
            <TextInput
              label="Which mobile network are you with?"
              id="MobileNetwork"
              required={true}
            />
          </div>
          <div className="mb-3">
            <TextInput
              label="MMP Patient Engagement Group want to help you manage your health online. Please choose all that apply."
              id="MMPGroup"
              required={true}
            />
          </div>
          <div className="mb-3">
            <TextInput
              label="please enter your email address"
              id="EmailAddress"
              required={true}
            />
          </div>
          <div className="mb-3">
            <DropdownInput
              label="NDB Network"
              id="NDBNetworkTypes"
              required={true}
              items={ndbnetworktypes}
              itemlabel="NDBNetworkType"
              itemvalue="NDBNetworkTypeID"
            />
          </div>
        </Col>
        <Col sm="4">
          <div className="mb-2">&nbsp;</div>
          <div className="mb-3">
            <ToggleInput label="Entered on NDB" id="NDBAdded" />
          </div>
          <div className="mb-3">
            <TextInput
              label="Enter your last name"
              id="LastName"
              required={true}
            />
          </div>
          <div className="mb-3">
            <DropdownInput
              label="Select your gender"
              id="Genders"
              required={true}
              items={genders}
              itemlabel="Gender"
              itemvalue="GenderID"
            />
          </div>
          <div className="mb-3">
            <TextInput
              label="Enter the 1st line of your address"
              id="Address"
              required={true}
            />
          </div>
          <div className="mb-3">
            <TextInput
              label="Enter your postcode"
              id="Postcode"
              placeholder="e.g. M1, B1"
              required={true}
            />
          </div>
          <div className="mb-3">
            <NumberInput
              label="What is your contact number?"
              id="ContactNumber"
              required={true}
            />
          </div>
          <div className="mb-3">
            <DropdownInput
              label="About your mobile phone contract. Please choose from the following."
              id="PhoneContracts"
              required={true}
              items={phonecontracts}
              itemlabel="PhoneContract"
              itemvalue="PhoneContractID"
            />
          </div>
          <div className="mb-2">&nbsp;</div>
          <div className="mb-2">&nbsp;</div>
          <div className="mb-3">
            <ToggleInput label="Do you have a smoke alarm?" id="SmokeAlarm" />
          </div>
          <div className="mb-3">
            <NumberInput
              label="New NDB Number"
              id="NDBNumber"
              required={true}
            />
          </div>
          <div className="mb-2">&nbsp;</div>
          <div className="mb-3">
            <ToggleInput label="NDB Registered" id="NDBRegistered" />
          </div>
        </Col>
      </Row>
      <Row>
        <div className="mb-3">
          <ToggleInput label="Active" id="IsActive" />
        </div>
      </Row>
      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" className="btn" onClick={handleSave}>
          {isEmpty(healthday) ? "Save" : "Update"}
        </Button>
        <Button
          type="submit"
          color="secondary"
          onClick={onCancelClick}
          className=""
        >
          Cancel
        </Button>
      </div>
    </FormProvider>
  )
}
export default BasicInfoTab
